import * as actionTypes from './actionTypes';
import checkHttpError from './checkHttpError';
import responseToJson from './responseToJson';
import { attachRecaptchaToken } from './attachRecaptchaToken';
import { setAuthToken } from './authTokenActions';
import clearLocalSession from '../lib/clearLocalSession';
import Config from '../Config';

const config = new Config();

export function fetchUserRequest() {
  return { type: actionTypes.FETCH_USER_REQUEST };
}

export function fetchUserError(error) {
  return { type: actionTypes.FETCH_USER_ERROR, error };
}

export function fetchUserSuccess(user) {
  return { type: actionTypes.FETCH_USER_SUCCESS, user };
}

export function submitLogoutRequest() {
  return { type: actionTypes.USER_LOGOUT_REQUEST };
}

export function submitLogoutError(error) {
  return { type: actionTypes.USER_LOGOUT_ERROR, error };
}

export function submitLogoutSuccess() {
  return { type: actionTypes.USER_LOGOUT_SUCCESS };
}

export function userTokenRefreshRequest() {
  return { type: actionTypes.USER_TOKEN_REFRESH_REQUEST };
}

export function userTokenRefreshError(error) {
  return { type: actionTypes.USER_TOKEN_REFRESH_ERROR, error };
}

export function userTokenRefreshSuccess() {
  return { type: actionTypes.USER_TOKEN_REFRESH_SUCCESS };
}

function logoutOptions(authToken) {
  return {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authToken,
    },
    mode: 'cors',
  };
}

export function userLogout(authToken) {
  return (dispatch) => {
    dispatch(submitLogoutRequest());

    return fetch(`${config.apiUrl}/users/logout`, logoutOptions(authToken))
      .then(checkHttpError)
      .then(dispatch(submitLogoutSuccess()))
      .catch((error) => {
        dispatch(submitLogoutError(error));
      });
  };
}

// For a logout that is immediately followed by leaving the app. userLogout resets the whole store
// as soon as it is dispatched, but the page that triggered it stays on screen until the next site
// has loaded, so it re-renders from an empty store for that whole wait -- the entity "I'm
// Finished" page turned into the individual thank-you page on its way to TS Learn. This clears
// the browser session straight away, so nothing outlives the logout, and leaves the store alone
// until the page is actually being left. It always settles: a refused logout must not trap the
// applicant on a page they have finished with.
export function userLogoutBeforeLeaving(authToken) {
  return (dispatch) => {
    dispatch(submitLogoutRequest());
    clearLocalSession();
    window.addEventListener('pagehide', () => dispatch(submitLogoutSuccess()), { once: true });

    return fetch(`${config.apiUrl}/users/logout`, logoutOptions(authToken))
      .then(checkHttpError)
      .catch(error => dispatch(submitLogoutError(error)));
  };
}

export function fetchUser(authToken) {
  return (dispatch) => {
    dispatch(fetchUserRequest());
    const options = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: authToken,
      },
      mode: 'cors',
    };

    return fetch(`${config.apiUrl}/users/userIdInfo`, options)
      .then(checkHttpError)
      .then(responseToJson)
      .then(json => dispatch(fetchUserSuccess(json)))
      .catch(error => dispatch(fetchUserError(error)));
  };
}

export function userTokenRefresh(authToken) {
  return (dispatch) => {
    dispatch(userTokenRefreshRequest());
    return attachRecaptchaToken({}, 'token')
      .then((data) => {
        const options = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
          mode: 'cors',
          body: JSON.stringify(data),
        };
        return fetch(`${config.apiUrl}/users/refresh`, options);
      })
      .then(checkHttpError)
      .then(responseToJson)
      .then((json) => {
        dispatch(userTokenRefreshSuccess());
        dispatch(setAuthToken(json.token.id));
      })
      .catch((error) => {
        dispatch(userTokenRefreshError(error));
        return Promise.reject(error);
      });
  };
}

